<template>
  <woot-modal :show="show" :on-close="onClose">
    <woot-modal-header
      :header-title="$t('BUSINESS_DATA.FILE_SOURCES.ADD_NEW')"
    />
    <form class="flex flex-col modal-content" @submit.prevent="handleSubmit">
      <div class="space-y-5">
        <div class="space-y-2">
          <woot-input
            v-model="form.name"
            :label="$t('BUSINESS_DATA.FILE_SOURCES.NAME')"
            :required="true"
            :placeholder="$t('BUSINESS_DATA.FILE_SOURCES.NAME_PLACEHOLDER')"
            :error="v$.form.name.$error ? $t('BUSINESS_DATA.FILE_SOURCES.NAME_ERROR') : ''"
            :class="{ error: v$.form.name.$error }"
            type="text"
            maxlength="120"
            @blur="v$.form.name.$touch"
          />
        </div>

        <div class="space-y-2">
          <FormLabel
            :label="$t('BUSINESS_DATA.FILE_SOURCES.FILE')"
            :required="true"
          />
          <input
            ref="fileInput"
            type="file"
            class="block w-full text-sm text-slate-900 dark:text-slate-100"
            required
            @change="onFileSelected"
          />
          <p class="text-xs text-slate-500 dark:text-slate-400">
            {{ $t('BUSINESS_DATA.FILE_SOURCES.FILE_HELP') }}
          </p>
          <p v-if="form.fileNames.length" class="text-xs text-emerald-600 dark:text-emerald-300">
            {{ form.fileNames.join(', ') }}
          </p>
        </div>
      </div>

      <div class="flex justify-center gap-3 pt-4">
        <woot-button type="button" variant="smooth" color-scheme="ghost" @click="onClose">
          {{ $t('COMMON.CANCEL') }}
        </woot-button>
        <woot-button type="submit" variant="smooth" color-scheme="success" :is-loading="isSubmitting" :disabled="!isFormValid">
          {{ $t('COMMON.CREATE') }}
        </woot-button>
      </div>
    </form>
  </woot-modal>
</template>

<script>
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import FormLabel from 'dashboard/components/widgets/forms/FormLabel.vue';

export default {
  name: 'FileSourceModal',
  components: {
    FormLabel,
  },
  props: {
    show: { type: Boolean, default: false },
  },
  emits: ['close', 'saved'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      isSubmitting: false,
      form: {
        name: '',
        files: [],
        fileNames: [],
      },
    };
  },
  validations() {
    return {
      form: {
        name: { required },
      },
    };
  },
  computed: {
    isFormValid() {
      return !this.v$.$invalid && this.form.files.length > 0;
    },
  },
  watch: {
    show(newValue) {
      if (!newValue) {
        this.resetForm();
      }
    },
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    onFileSelected(event) {
      const files = Array.from(event.target.files || []);
      if (!files.length) {
        this.form.files = [];
        this.form.fileNames = [];
        return;
      }

      const maxSizeMb = 15;
      const maxSizeBytes = maxSizeMb * 1024 * 1024;
      const validFiles = [];
      let hasInvalidFiles = false;

      files.forEach(fileItem => {
        if (fileItem.size > maxSizeBytes) {
          hasInvalidFiles = true;
          return;
        }
        validFiles.push(fileItem);
      });

      if (hasInvalidFiles) {
        useAlert(
          this.$t('BUSINESS_DATA.FILE_SOURCES.ERRORS.FILE_TOO_LARGE', { size: maxSizeMb }),
          { type: 'error' }
        );
      }

      if (!validFiles.length) {
        event.target.value = '';
        this.form.files = [];
        this.form.fileNames = [];
        return;
      }

      const [firstValidFile] = validFiles;
      this.form.files = firstValidFile ? [firstValidFile] : [];
      this.form.fileNames = firstValidFile ? [firstValidFile.name] : [];
    },
    resetForm() {
      this.form = {
        name: '',
        files: [],
        fileNames: [],
      };
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
      this.v$.$reset();
      this.isSubmitting = false;
    },
    async handleSubmit() {
      this.v$.$touch();
      if (this.v$.$invalid || !this.isFormValid || this.isSubmitting) {
        return;
      }
      this.isSubmitting = true;
      try {
        const accountId = this.$store.getters.getCurrentAccountId;
        const formData = new FormData();
        formData.append('file_source[name]', this.form.name.trim());
        const [file] = this.form.files;
        if (file) {
          formData.append('file_source[file]', file);
        }
        await window.axios.post(`/api/v1/accounts/${accountId}/file_sources`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        useAlert(this.$t('BUSINESS_DATA.FILE_SOURCES.CREATED_SUCCESS'), { type: 'success' });
        this.$emit('saved');
        this.resetForm();
      } catch (error) {
        console.error('Failed to create file source:', error);
        const message = error.response?.data?.errors?.join(', ') || this.$t('BUSINESS_DATA.FILE_SOURCES.ERRORS.CREATE_FAILED');
        useAlert(message, { type: 'error' });
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>
